import { useEffect, useRef } from 'react'

/** The platform long-press length: any shorter and a slow scroll start lifts a card. */
const LONG_PRESS_MS = 500
/** Under the browsers' own pan slop, so a scroll that cancels the press still starts. */
const SCROLL_SLOP_PX = 8
const EDGE_SCROLL_ZONE_PX = 56
const EDGE_SCROLL_MAX_STEP_PX = 16

/**
 * Touch drag for the board. Mobile browsers never fire native drag events from
 * a finger, so a long press on a `data-board-card` lifts a copy under the
 * finger and the release drops it on the `data-board-drop` target beneath.
 * Holding near either edge of `data-board-scroll` scrolls the columns along.
 */
export function useTouchCardDrag(
  board: HTMLElement | null,
  handlers: {
    onLift: (contactId: string) => void
    onOver: (dropKey: string | undefined) => void
    onDrop: (contactId: string, dropKey: string) => void
    onCancel: () => void
  },
) {
  // Read at event time: re-binding the listeners mid-gesture would drop the gesture.
  const latest = useRef(handlers)
  useEffect(() => {
    latest.current = handlers
  })

  useEffect(() => {
    if (!board) return
    let timer: ReturnType<typeof setTimeout> | undefined
    let press: { id: string; card: HTMLElement; x: number; y: number } | undefined
    let lifted:
      | { id: string; ghost: HTMLElement; offsetX: number; offsetY: number; x: number; y: number; key?: string }
      | undefined
    let frame = 0

    const reset = () => {
      clearTimeout(timer)
      cancelAnimationFrame(frame)
      lifted?.ghost.remove()
      press = undefined
      lifted = undefined
    }

    const retarget = () => {
      if (!lifted) return
      // The ghost is `pointer-events: none`, so this sees the target under it.
      const key =
        document.elementFromPoint(lifted.x, lifted.y)?.closest<HTMLElement>(`[data-board-drop]`)?.dataset.boardDrop ??
        undefined
      if (key === lifted.key) return
      lifted.key = key
      latest.current.onOver(key)
    }

    // Runs every frame while lifted: a finger held still at the edge sends no
    // touchmove, and the target under it changes as the columns slide past.
    const edgeScroll = () => {
      if (!lifted) return
      const scroller = board.querySelector<HTMLElement>(`[data-board-scroll]`)
      if (scroller) {
        const rect = scroller.getBoundingClientRect()
        const left = Math.max(rect.left, 0) + EDGE_SCROLL_ZONE_PX - lifted.x
        const right = lifted.x - (Math.min(rect.right, window.innerWidth) - EDGE_SCROLL_ZONE_PX)
        const depth = left > 0 ? -left : Math.max(right, 0)
        if (depth) {
          scroller.scrollLeft +=
            Math.sign(depth) *
            Math.min(EDGE_SCROLL_MAX_STEP_PX, (Math.abs(depth) / EDGE_SCROLL_ZONE_PX) * EDGE_SCROLL_MAX_STEP_PX)
          retarget()
        }
      }
      frame = requestAnimationFrame(edgeScroll)
    }

    const lift = () => {
      if (!press) return
      const rect = press.card.getBoundingClientRect()
      const ghost = press.card.cloneNode(true) as HTMLElement
      Object.assign(ghost.style, {
        position: `fixed`,
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        margin: `0`,
        zIndex: `9999`,
        pointerEvents: `none`,
        opacity: `0.92`,
        transform: `rotate(2deg)`,
        boxShadow: `0 12px 28px rgba(0, 0, 0, 0.18)`,
      })
      document.body.appendChild(ghost)
      lifted = {
        id: press.id,
        ghost,
        offsetX: press.x - rect.left,
        offsetY: press.y - rect.top,
        x: press.x,
        y: press.y,
      }
      navigator.vibrate?.(10)
      latest.current.onLift(press.id)
      frame = requestAnimationFrame(edgeScroll)
    }

    const onTouchStart = (event: TouchEvent) => {
      reset()
      if (event.touches.length !== 1) return
      const card = (event.target as Element).closest<HTMLElement>(`[data-board-card]`)
      const id = card?.dataset.boardCard
      if (!card || !id) return
      const touch = event.touches[0]
      press = { id, card, x: touch.clientX, y: touch.clientY }
      timer = setTimeout(lift, LONG_PRESS_MS)
    }

    const onTouchMove = (event: TouchEvent) => {
      if (!press) return
      const touch = event.touches[0]
      if (!lifted) {
        if (Math.hypot(touch.clientX - press.x, touch.clientY - press.y) > SCROLL_SLOP_PX) reset()
        return
      }
      event.preventDefault()
      lifted.x = touch.clientX
      lifted.y = touch.clientY
      lifted.ghost.style.left = `${touch.clientX - lifted.offsetX}px`
      lifted.ghost.style.top = `${touch.clientY - lifted.offsetY}px`
      retarget()
    }

    const onTouchEnd = (event: TouchEvent) => {
      if (!lifted) return reset()
      // Otherwise the release also lands as a click and opens the drawer.
      event.preventDefault()
      const { id, key } = lifted
      reset()
      if (key) latest.current.onDrop(id, key)
      else latest.current.onCancel()
    }

    const onTouchCancel = () => {
      const wasLifted = !!lifted
      reset()
      if (wasLifted) latest.current.onCancel()
    }

    // Android's long-press context menu would end the drag.
    const onContextMenu = (event: Event) => {
      if (press) event.preventDefault()
    }

    board.addEventListener(`touchstart`, onTouchStart, { passive: true })
    board.addEventListener(`contextmenu`, onContextMenu)
    // On window: a card re-rendered mid-drag stops bubbling its touches to the board.
    window.addEventListener(`touchmove`, onTouchMove, { passive: false })
    window.addEventListener(`touchend`, onTouchEnd, { passive: false })
    window.addEventListener(`touchcancel`, onTouchCancel)
    return () => {
      reset()
      board.removeEventListener(`touchstart`, onTouchStart)
      board.removeEventListener(`contextmenu`, onContextMenu)
      window.removeEventListener(`touchmove`, onTouchMove)
      window.removeEventListener(`touchend`, onTouchEnd)
      window.removeEventListener(`touchcancel`, onTouchCancel)
    }
  }, [board])
}
